import { IMAGE_RESIZE_SPEC } from '@core/image'
import {
    AuthorizationIcon,
    Avatar,
    AvatarFallback,
    AvatarImage,
    Menu,
    MenuContent,
    MenuItem,
    MenuTrigger,
} from '@ds/components'
import { MenuLinkItem } from '../menu-link-item'
import type { THeaderLabels, TMenuItemView, TSiteHeaderUser } from '../types'
import styles from './UserMenu.module.css'

type TUserMenuProps = {
    user: TSiteHeaderUser
    items: TMenuItemView[]
    labels: Pick<THeaderLabels, 'openMenu' | 'logOut' | 'new'>
    isDropdown: boolean
    onLogout: () => void
    onOpenAccountPanel: () => void
}

const renderAvatar = (user: TSiteHeaderUser) =>
    user.avatarUrl ? (
        <Avatar className={styles.avatar}>
            <AvatarImage
                src={user.avatarUrl}
                alt={user.displayName}
                width={36}
                height={36}
                resize={IMAGE_RESIZE_SPEC}
            />
            <AvatarFallback />
        </Avatar>
    ) : (
        <Avatar className={styles.avatar} theme="experiment">
            <AvatarFallback theme="experiment" />
        </Avatar>
    )

export const UserMenu = ({
    user,
    items,
    labels,
    isDropdown,
    onLogout,
    onOpenAccountPanel,
}: TUserMenuProps) => {
    if (!isDropdown) {
        return (
            <button
                type="button"
                className={styles.userTrigger}
                aria-label={labels.openMenu}
                onClick={onOpenAccountPanel}
            >
                {renderAvatar(user)}
            </button>
        )
    }

    return (
        <Menu modal={false}>
            <MenuTrigger asChild>
                <button
                    type="button"
                    className={styles.userTrigger}
                    aria-label={labels.openMenu}
                    data-test="user-dropdown-trigger"
                >
                    {renderAvatar(user)}
                </button>
            </MenuTrigger>
            <MenuContent className={styles.dropdown} align="end" sideOffset={12}>
                <div className={styles.menuDisplayName}>{user.displayName}</div>
                {items.map(item => (
                    <MenuItem key={item.href} asChild>
                        <MenuLinkItem item={item} newLabel={labels.new} />
                    </MenuItem>
                ))}
                <MenuItem className={styles.logoutItem} onSelect={onLogout}>
                    <AuthorizationIcon className={styles.dropdownIcon} />
                    <span>{labels.logOut}</span>
                </MenuItem>
            </MenuContent>
        </Menu>
    )
}
